/**
 * 260913-l8n / MKS-174 — pure helpers for the milestone dialog's derived
 * status preview. Co-located with `milestoneCounts.ts` (same directory,
 * same "pure helper extracted from the component, unit-tested on its own"
 * convention).
 *
 * CONTRACT: `deriveMilestoneStatus` here must stay byte-for-byte equivalent
 * in behavior to `deriveMilestoneStatus` in
 * `backend/src/services/milestoneService.ts`. A change to one requires a
 * change to the other — drift here would make the create-dialog preview
 * lie about what the server will actually return.
 */
import { formatDisplayDate } from '../../utils/format';

const ISO_DAY_REGEX = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Parse a strict YYYY-MM-DD string into UTC-midnight epoch millis, rejecting
 * anything that doesn't round-trip through `getUTC*` (e.g. month 13, day 40)
 * — mirrors the backend's `parseDate`. Returns null for anything that isn't
 * a clean calendar date, INCLUDING a partially-typed value; this runs on
 * every keystroke so it must never throw.
 */
function parseStrictUTCDay(value: string | null | undefined): number | null {
  if (!value) return null;
  const m = ISO_DAY_REGEX.exec(value);
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  const utc = Date.UTC(year, month - 1, day);
  const dt = new Date(utc);
  if (
    dt.getUTCFullYear() !== year ||
    dt.getUTCMonth() + 1 !== month ||
    dt.getUTCDate() !== day
  ) {
    return null;
  }
  return utc;
}

/**
 * Mirrors the backend's `deriveMilestoneStatus`, but takes the dialog's
 * local completion toggle instead of a stored status string:
 *   - isCompleted true                       => 'completed' (wins, even future)
 *   - isCompleted false + no/malformed date  => 'planned'
 *   - isCompleted false + date in the future => 'planned'
 *   - isCompleted false + date today/past    => 'active'
 *
 * `now` defaults to a Date constructed inside the function body, never a
 * module-level constant.
 */
export function deriveMilestoneStatus(
  isCompleted: boolean,
  startDate: string | null | undefined,
  now: Date = new Date(),
): 'active' | 'completed' | 'planned' {
  if (isCompleted) return 'completed';
  const startDay = parseStrictUTCDay(startDate);
  if (startDay === null) return 'planned';
  const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  return startDay > today ? 'planned' : 'active';
}

/**
 * Display string for the modal's read-only status line.
 */
export function describeMilestoneStatus(
  status: 'active' | 'completed' | 'planned',
  startDate: string | null | undefined,
): string {
  if (status === 'completed') return 'Completed';
  if (status === 'active') return 'Active';
  // planned
  const parsed = parseStrictUTCDay(startDate);
  if (parsed === null) return 'Planned';
  return `Planned — starts ${formatDisplayDate(startDate)}`;
}
